/**
 * The terminal controller: one place that owns the socket, the session and the
 * polling loop, so React components only read state and call verbs.
 *
 * ## Why imperative
 *
 * A trading terminal has exactly one socket and exactly one cursor. Driving that
 * from component effects makes the connection's lifetime depend on mount order
 * and on React's development-mode double-rendering — which, for a socket with a
 * replay cursor, is how you get two replays and a duplicated position. The
 * controller is created once (module scope), started once, and stopped on
 * logout.
 *
 * ## What it does per second
 *
 * - **Quotes**: pushed by the server, batched by the quote board.
 * - **Account snapshot**: polled from `POST /preview` every 1.5 s. `equity`,
 *   `margin level` and per-position floating P&L are server numbers; the only
 *   alternative would be computing them in the browser, which is the one thing
 *   the brief forbids. A P&L stream frame is the right long-term fix and is
 *   recorded as an open question in the handoff.
 * - **Token**: refreshed silently 90 s before expiry.
 * - **Deals**: refetched after every command and when the history panel asks.
 */

import type { PreviewSymbol } from '@weke/protocol';
import type { AlertPayload, PreviewRequest, PreviewResponse, ReplayDonePayload, ResponsePayload } from '@weke/protocol';
import type { HelloPayload, ServiceErrorPayload } from '@weke/protocol';

import { downloadStatementFile, fetchAllDeals, fetchBars, fetchPreview, login as loginRequest, rebuildBars, refresh } from '@weke/ui';
import { ApiError } from '@weke/ui';
import { ProtocolClient } from '@weke/ui';
import type { ConnectionStatus, ProtocolFailure } from '@weke/ui';
import { isExpired, loadSession, needsRefresh, saveSession } from '@weke/ui';
import type { Session } from '@weke/ui';
import { API } from '@weke/ui';
import { appStore, quoteBoard } from '@weke/ui';
import { applyEvent, applyReplayDone, initialBook } from '@weke/ui';
import { explainAlert, explainRejection, explainServiceError } from '@weke/ui';
import { offsetMs, serverDate, startOfDayIso, isoMinutesBefore } from '@weke/ui';
import type { MinuteBar } from '@weke/ui';
import { withRunningBalance } from '@weke/ui';

const SNAPSHOT_POLL_MS = 1_500;
/** At most one bar rebuild per symbol per this window (see ars). */
const REBUILD_THROTTLE_MS = 5_000;
const DAY_OPEN_REFRESH_MS = 300_000;

export type LoginResult = { readonly ok: true } | { readonly ok: false; readonly message: string };

class TerminalController {
  private client: ProtocolClient | null = null;
  private snapshotTimer: ReturnType<typeof setInterval> | null = null;
  private dayOpenTimer: ReturnType<typeof setInterval> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private pendingPreview: AbortController | null = null;
  /** Last M1-bar rebuild per symbol, to throttle the read-model rebuild. */
  private readonly rebuiltBars = new Map<string, number>();

  /** Boot: restore a session if one is stored, then connect. */
  start(): void {
    if (this.started) return;
    this.started = true;
    const session = loadSession();
    if (session === null) {
      appStore.patch({ status: 'offline', statusDetail: 'not signed in' });
      return;
    }
    if (isExpired(session, Date.now())) {
      void this.refreshSession(session);
      return;
    }
    appStore.patch({ session });
    this.afterLogin(session);
  }

  stop(): void {
    this.started = false;
    this.client?.close();
    this.client = null;
    if (this.snapshotTimer !== null) clearInterval(this.snapshotTimer);
    if (this.dayOpenTimer !== null) clearInterval(this.dayOpenTimer);
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    this.snapshotTimer = null;
    this.dayOpenTimer = null;
    this.refreshTimer = null;
  }

  /* ---------------------------------------------------------------- */
  /* Session                                                           */
  /* ---------------------------------------------------------------- */

  async login(tenantSlug: string, loginValue: string, password: string): Promise<LoginResult> {
    try {
      const session = await loginRequest(tenantSlug, loginValue, password);
      saveSession(session);
      appStore.clear();
      appStore.patch({ session });
      this.afterLogin(session);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError) {
        const message =
          error.code === 'E_AUTH'
            ? error.message
            : `The platform refused the login (${error.code}).`;
        return { ok: false, message };
      }
      return {
        ok: false,
        message: `Cannot reach the platform at this address: ${(error as Error).message}`,
      };
    }
  }

  logout(): void {
    this.stop();
    saveSession(null);
    quoteBoard.reset();
    appStore.clear();
    appStore.patch({ statusDetail: 'signed out' });
    this.started = false;
    this.start();
  }

  private afterLogin(session: Session): void {
    this.stop();
    this.started = true;
    appStore.patch({ status: 'connecting', statusDetail: null, book: initialBook() });
    this.client = new ProtocolClient({
      url: (token) => `${API.ws}?token=${encodeURIComponent(token)}`,
      token: () => appStore.getSnapshot().session?.accessToken ?? null,
      cursor: () => appStore.getSnapshot().book.cursor,
      symbols: () => appStore.getSnapshot().symbols.map((symbol) => symbol.name),
      handlers: {
        onHello: (payload, receivedAt) => this.onHello(payload, receivedAt),
        onEvent: (event) => {
          const state = appStore.getSnapshot();
          const next = applyEvent(state.book, event);
          appStore.patch({
            book: next,
            lastMessageAt: event.at,
            // A gap means our book has a hole; ask for the missing range.
            statusDetail: next.gapFrom === null ? null : `gap after ${next.gapFrom}`,
          });
          if (next.gapFrom !== null) {
            this.client?.requestResume(next.gapFrom);
          }
        },
        onResponse: () => undefined,
        onAlert: (payload) => this.onAlert(payload),
        onError: (payload) => this.onProtocolError(payload),
        onReplayDone: (payload) => this.onReplayDone(payload),
        onQuote: (quote) => quoteBoard.ingest(quote),
        onStatus: (status, detail) => this.onStatus(status, detail),
        onPong: (rtt) => appStore.patch({ latencyMs: rtt }),
        onGap: (cursor) => this.client?.requestResume(cursor),
        onFatal: (failure) => void this.onFatal(failure),
      },
    });
    this.client.connect();
    this.scheduleRefresh(session);
    this.startPolling();
  }

  private async refreshSession(session: Session): Promise<void> {
    try {
      const next = await refresh(session);
      saveSession(next);
      appStore.patch({ session: next });
      this.afterLogin(next);
    } catch {
      // A refused refresh is the only path back to the login screen.
      saveSession(null);
      this.stop();
      this.started = false;
      appStore.clear();
      appStore.patch({
        status: 'offline',
        statusDetail: 'session expired',
      });
      appStore.pushBanner({
        tone: 'warning',
        title: 'Session expired',
        detail: 'Sign in again. Your layout is kept.',
        code: 'E_AUTH',
      });
    }
  }

  private scheduleRefresh(session: Session): void {
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    const delay = Math.max(5_000, session.expiresAtMs - Date.now() - 90_000);
    this.refreshTimer = setTimeout(() => {
      const current = appStore.getSnapshot().session;
      if (current === null) return;
      if (needsRefresh(current, Date.now())) void this.refreshSession(current);
      else this.scheduleRefresh(current);
    }, delay);
  }

  private async onFatal(failure: ProtocolFailure): Promise<void> {
    const session = appStore.getSnapshot().session;
    if (failure.code === 'E_AUTH' && session !== null) {
      await this.refreshSession(session);
      return;
    }
    appStore.pushBanner({
      tone: 'error',
      title: explainServiceError(failure.code).title,
      detail: failure.message,
      code: failure.code,
    });
  }

  /* ---------------------------------------------------------------- */
  /* Selection                                                         */
  /* ---------------------------------------------------------------- */

  /** Chart and ticket follow the market watch's selection. */
  selectSymbol(name: string): void {
    if (appStore.getSnapshot().selectedSymbol === name) return;
    appStore.patch({ selectedSymbol: name });
  }

  /* ---------------------------------------------------------------- */
  /* Server messages                                                   */
  /* ---------------------------------------------------------------- */

  private onHello(payload: HelloPayload, receivedAtMs: number): void {
    appStore.patch({
      serverOffsetMs: offsetMs(payload.server_time, receivedAtMs),
      heartbeatMs: payload.heartbeat_ms,
      accepting: payload.accepting,
      lastMessageAt: payload.server_time,
      statusDetail: payload.refusal,
    });
    if (!payload.accepting) {
      appStore.pushBanner({
        tone: 'error',
        title: 'Trading is suspended on this account',
        detail: payload.refusal ?? 'The engine refused to load the account. Contact your broker.',
        code: 'ENGINE_REFUSED',
      });
    }
  }

  private onStatus(status: ConnectionStatus, detail: string | null): void {
    const current = appStore.getSnapshot();
    appStore.patch({
      status,
      statusDetail: detail ?? (status === 'live' ? null : current.statusDetail),
    });
    if (status === 'live' && current.status !== 'live') {
      // The book and the account both need a refresh after a reconnect: quotes
      // resume from the feed, and the snapshot may have moved while we were out.
      void this.pollSnapshot();
    }
  }

  private onReplayDone(payload: ReplayDonePayload): void {
    const book = appStore.getSnapshot().book;
    appStore.setBook(
      applyReplayDone(
        book,
        payload,
        new Date().toISOString(),
        this.client?.lastResumeFrom ?? book.cursor.toString(),
      ),
    );
  }

  private onAlert(payload: AlertPayload): void {
    appStore.pushAlert({
      id: `alert-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      kind: payload.kind,
      detail: payload.detail,
      at: new Date().toISOString(),
    });
    appStore.pushBanner({
      tone: payload.kind === 'MARGIN_CALL' || payload.kind === 'STOP_OUT' ? 'warning' : 'info',
      title: explainAlert(payload.kind),
      detail: payload.detail,
      code: payload.kind,
    });
  }

  private onProtocolError(payload: ServiceErrorPayload): void {
    const explanation = explainServiceError(payload.code);
    appStore.pushBanner({
      tone: payload.code === 'E_UNAVAILABLE' ? 'warning' : 'error',
      title: explanation.title,
      detail: payload.message,
      code: payload.code,
    });
    if (payload.code === 'E_AUTH') {
      const session = appStore.getSnapshot().session;
      if (session !== null) void this.refreshSession(session);
    }
  }

  /* ---------------------------------------------------------------- */
  /* Read-only polls                                                   */
  /* ---------------------------------------------------------------- */

  private startPolling(): void {
    if (this.snapshotTimer !== null) clearInterval(this.snapshotTimer);
    this.snapshotTimer = setInterval(() => void this.pollSnapshot(), SNAPSHOT_POLL_MS);
    void this.pollSnapshot();
    if (this.dayOpenTimer !== null) clearInterval(this.dayOpenTimer);
    this.dayOpenTimer = setInterval(() => void this.loadDayOpen(), DAY_OPEN_REFRESH_MS);
  }

  private token(): string | null {
    return appStore.getSnapshot().session?.accessToken ?? null;
  }

  /** Apply a preview response: the account snapshot, and the symbol catalogue. */
  private applySnapshot(response: PreviewResponse): void {
    const state = appStore.getSnapshot();
    const symbols = response.symbols;
    appStore.patch({
      account: response.account,
      // The instant every position mark in `account` was valued at. The
      // positions table renders `Now` and `P&L` from that same snapshot, so this
      // is the honest "as of" for both columns.
      snapshotAt: response.server_time,
      // The catalogue is static in practice; replacing the array on every poll
      // would hand every component a new object each 1.5 s and reset the order
      // ticket's prices in the middle of being typed. The reference changes only
      // when the catalogue really changed.
      symbols: sameSymbols(state.symbols, symbols) ? state.symbols : symbols,
      lastMessageAt: response.server_time,
      selectedSymbol:
        state.selectedSymbol ?? (symbols[0] === undefined ? null : symbols[0].name),
    });
    if (state.symbols.length === 0) {
      // First snapshot: subscribe to the catalogue's quotes.
      this.client?.subscribe(symbols.map((symbol) => symbol.name));
      void this.loadDayOpen();
    }
  }

  async pollSnapshot(): Promise<void> {
    const token = this.token();
    if (token === null) return;
    try {
      const response = await fetchPreview(token);
      this.applySnapshot(response);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        const session = appStore.getSnapshot().session;
        if (session !== null) void this.refreshSession(session);
      }
      // Any other failure is transient; the next poll will try again and the
      // status bar already shows the connection state.
    }
  }

  /** `POST /preview` with an order attached: the ticket's margin preview. */
  async preview(place: PreviewRequest['place']): Promise<PreviewResponse | null> {
    const token = this.token();
    if (token === null) return null;
    this.pendingPreview?.abort();
    const controller = new AbortController();
    this.pendingPreview = controller;
    try {
      const response = await fetchPreview(token, place);
      if (!controller.signal.aborted) this.applySnapshot(response);
      return response;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        const session = appStore.getSnapshot().session;
        if (session !== null) void this.refreshSession(session);
      }
      return null;
    } finally {
      if (this.pendingPreview === controller) this.pendingPreview = null;
    }
  }

  /**
   * The session reference price per symbol, for the change column: the first M1
   * bar of the server day. Absent means "no reference yet", not zero — a change
   * of `0.00` would claim the market has not moved.
   */
  async loadDayOpen(): Promise<void> {
    const token = this.token();
    const state = appStore.getSnapshot();
    if (token === null || state.symbols.length === 0) return;
    const now = Date.now() + state.serverOffsetMs;
    // One bar, deliberately: the *first* bar of the server day is the session
    // reference the change column subtracts from. This is not the chart's fetch.
    const from = startOfDayIso(serverDate(state.serverOffsetMs, Date.now()));
    const to = new Date(now).toISOString();
    const dayOpen: Record<string, string> = { ...state.dayOpen };
    await Promise.all(
      state.symbols.map(async (symbol) => {
        try {
          let bars = await fetchBars(token, { symbol: symbol.name, from, to, limit: 1 });
          const lastRebuild = this.rebuiltBars.get(symbol.name) ?? 0;
          if (bars.length === 0 && Date.now() - lastRebuild > REBUILD_THROTTLE_MS) {
            // Same P4 gap as the chart: bars exist only after a rebuild.
            this.rebuiltBars.set(symbol.name, Date.now());
            await rebuildBars(token, { symbol: symbol.name, from, to });
            bars = await fetchBars(token, { symbol: symbol.name, from, to, limit: 1 });
          }
          const first = bars[0];
          if (first !== undefined) dayOpen[symbol.name] = first.open_e8;
        } catch {
          // The change column shows a dash rather than a guess.
        }
      }),
    );
    appStore.patch({ dayOpen });
  }

  /**
   * Bars for the chart, aggregated by the caller.
   *
   * `history` returns the **oldest** `limit` bars of the requested window, so a
   * window wider than the limit silently pins the chart to the start of the
   * range (P5 shipped that: a 2-day window with `limit: 200` would freeze the
   * M1 chart on its first 200 minutes). The window is therefore sized to the
   * number of M1 bars asked for, which makes the result the **newest** bars and
   * respects `listBars`' 5,000-row cap instead of asking past it.
   */
  async bars(symbol: string, minutes: number): Promise<MinuteBar[]> {
    const token = this.token();
    if (token === null) return [];
    const state = appStore.getSnapshot();
    const now = Date.now() + state.serverOffsetMs;
    const m1Bars = Math.min(5_000, Math.max(120, minutes * 120));
    const from = isoMinutesBefore(now, m1Bars);
    const to = new Date(now).toISOString();
    const query = { symbol, from, to, limit: m1Bars };
    try {
      let bars = await fetchBars(token, query);
      // The history service stores ticks continuously but derives bars only on
      // request (P4 gap, docs/12). Rebuild at most every `REBUILD_THROTTLE_MS`
      // per symbol: the first attempt can legitimately find an empty tick table
      // (a stack that started a second ago), and a once-ever guard would leave
      // that chart empty until a reload.
      const lastRebuild = this.rebuiltBars.get(symbol) ?? 0;
      if (bars.length === 0 && Date.now() - lastRebuild > REBUILD_THROTTLE_MS) {
        this.rebuiltBars.set(symbol, Date.now());
        await rebuildBars(token, { symbol, from, to });
        bars = await fetchBars(token, query);
      }
      return bars;
    } catch {
      return [];
    }
  }

  /* ---------------------------------------------------------------- */
  /* History                                                           */
  /* ---------------------------------------------------------------- */

  /**
   * Reload the deal ledger and re-anchor its running balance.
   *
   * The anchor is the account balance **read after the deals**, because a
   * running balance is `balance_now − Σ deltas`: anchoring on a balance the
   * snapshot poll fetched a second earlier would shift every row by whatever
   * realised between the two (that is how the first version of this shipped, and
   * the acceptance test caught it as a 6.50 drift on a partial close).
   */
  async refreshDeals(): Promise<void> {
    const token = this.token();
    if (token === null) return;
    appStore.patch({ dealsLoading: true });
    try {
      const deals = await fetchAllDeals(token);
      const snapshot = await fetchPreview(token);
      this.applySnapshot(snapshot);
      appStore.patch({
        deals: withRunningBalance(deals, snapshot.account.balance_minor),
        dealsLoadedAt: new Date().toISOString(),
        dealsLoading: false,
      });
    } catch {
      appStore.patch({ dealsLoading: false });
    }
  }

  /**
   * Download the **official** statement from the platform.
   *
   * This is deliberately not the client-side CSV: the server folds the journal
   * (`services/recon`), proves I-6 and the line count, and returns byte-identical
   * bytes on every regeneration. If the account row has drifted from the ledger
   * the server refuses rather than issuing a statement that cannot tie out, and
   * the banner carries its code.
   */
  async downloadStatement(format: 'csv' | 'pdf' = 'pdf'): Promise<void> {
    const token = this.token();
    if (token === null) return;
    try {
      const { blob, filename } = await downloadStatementFile(token, format);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      const failure = error as { code?: string; message?: string };
      appStore.pushBanner({
        tone: 'error',
        title: 'Statement download failed',
        detail: failure.message ?? 'The platform refused to generate the statement.',
        code: failure.code ?? null,
      });
    }
  }

  /* ---------------------------------------------------------------- */
  /* Commands                                                          */
  /* ---------------------------------------------------------------- */

  private requireClient(): ProtocolClient {
    const client = this.client;
    if (client === null) {
      throw new ProtocolUnavailable('Not connected to the platform.');
    }
    return client;
  }

  /** Place an order; the response is returned so the ticket can react to it. */
  async place(place: NonNullable<PreviewRequest['place']>, clOrdId?: string): Promise<ResponsePayload> {
    return this.requireClient().command(
      {
        kind: 'PLACE_ORDER',
        symbol_id: place.symbol_id,
        side: place.side,
        type: place.type,
        tif: place.tif,
        volume: place.volume,
        ...(place.limit_price === undefined ? {} : { limit_price: place.limit_price }),
        ...(place.stop_price === undefined ? {} : { stop_price: place.stop_price }),
        // P12.1: the price this terminal last showed for the instrument is the
        // reference the server measures slippage against. Without it the
        // deviation control has nothing to compare to (docs/19 §3), so a market
        // order carries it. A limit/stop order already names its own price.
        ...this.expectedPriceFor(place),
        ...(place.sl === undefined ? {} : { sl: place.sl }),
        ...(place.tp === undefined ? {} : { tp: place.tp }),
      },
      clOrdId,
    );
  }

  /**
   * The price the trader saw, for a market order.
   *
   * The board's quote is what the ticket rendered (it subscribes to the same
   * stream), and it is stale-checked by the server regardless: an old board
   * price produces `E_PRICE_CHANGED`, which is the correct answer — the price
   * the trader saw is no longer available.
   */
  private expectedPriceFor(
    place: NonNullable<PreviewRequest['place']>,
  ): { expected_price?: string } {
    if (place.type !== 'MARKET') return {};
    const symbol = appStore.getSnapshot().symbols.find((entry) => entry.id === place.symbol_id);
    if (symbol === undefined) return {};
    const quote = quoteBoard.getSnapshot().quotes[symbol.name];
    if (quote === undefined) return {};
    return { expected_price: place.side === 'BUY' ? quote.ask : quote.bid };
  }

  /**
   * Close a position, wholly or partly.
   *
   * `volume` is omitted for a full close — the server closes what is there, which
   * is the only way to be sure the request matches the live volume (a stale tab
   * that sends its own idea of "all" is how half a position survives a close-all).
   */
  async closePosition(positionId: string, volumeE4?: string): Promise<ResponsePayload> {
    return this.requireClient().command({
      kind: 'CLOSE_POSITION',
      position_id: positionId,
      ...(volumeE4 === undefined ? {} : { volume: volumeE4 }),
    });
  }

  /**
   * Change a position's SL/TP.
   *
   * `undefined` means "leave it alone"; `null` means "clear it". The JSON
   * difference is the trap docs/10 §10 records: sending `null` where you meant
   * "unchanged" silently removes the trader's stop.
   */
  async modifyPosition(
    positionId: string,
    sl: string | null | undefined,
    tp: string | null | undefined,
  ): Promise<ResponsePayload> {
    return this.requireClient().command({
      kind: 'MODIFY_POSITION',
      position_id: positionId,
      ...(sl === undefined ? {} : { sl }),
      ...(tp === undefined ? {} : { tp }),
    });
  }

  async cancelOrder(orderId: string): Promise<ResponsePayload> {
    return this.requireClient().command({ kind: 'CANCEL_ORDER', order_id: orderId });
  }

  /** Close every open position, one command each (hedging: they are independent). */
  async closeAll(positionIds: readonly string[]): Promise<number> {
    let closed = 0;
    for (const positionId of positionIds) {
      try {
        const response = await this.closePosition(positionId);
        if (response.kind === 'POSITION_CLOSED') closed += 1;
      } catch {
        // One failure must not stop the rest; the banner reports it.
      }
    }
    return closed;
  }

  /** Report a response in the UI: a rejection as words, an acceptance as a toast. */
  reportResponse(response: ResponsePayload, successTitle: string): void {
    if (response.kind === 'REJECTED') {
      const explanation = explainRejection(response.code);
      appStore.pushBanner({
        tone: 'error',
        title: explanation.title,
        detail: explanation.detail,
        code: `${response.code} · ${explanation.source}`,
      });
      return;
    }
    if (response.kind === 'REQUOTE') {
      appStore.pushBanner({
        tone: 'warning',
        title: 'Price moved — confirm again',
        detail: 'The market moved beyond your tolerance. Nothing was filled; the new price is on the ticket.',
        code: 'REQUOTE',
      });
      return;
    }
    appStore.pushBanner({
      tone: 'success',
      title: successTitle,
      detail: '',
      code: null,
    });
  }
}

/** A local failure (no socket) that is not a server response. */
export class ProtocolUnavailable extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProtocolUnavailable';
  }
}

/** Deep-enough equality for the symbol catalogue (the fields the UI reads). */
function sameSymbols(
  left: readonly PreviewSymbol[],
  right: readonly PreviewSymbol[],
): boolean {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    const a = left[index];
    const b = right[index];
    if (a === undefined || b === undefined) return false;
    if (
      a.id !== b.id ||
      a.name !== b.name ||
      a.digits !== b.digits ||
      a.tick_size_e8 !== b.tick_size_e8 ||
      a.volume_min_e4 !== b.volume_min_e4 ||
      a.volume_max_e4 !== b.volume_max_e4 ||
      a.volume_step_e4 !== b.volume_step_e4 ||
      a.enabled !== b.enabled ||
      a.exec_mode !== b.exec_mode
    ) {
      return false;
    }
  }
  return true;
}

export const terminal = new TerminalController();


